import { Button, FormGroup, Input, Label, ListGroupItem } from "reactstrap";

import "../../range.css";
import { useSitePlan } from "./UseSitePlan";
import { useContext } from "react";
import { ToolContext } from "../../context/ToolContext";
import getAssetStyleInfo from "../../context/AssetStyleInfo";

const SitePlanForm = () => {
  const {
    toggleUniformScaling,
    sitePlan,
    toggleLockSitePlanPosition,
    resetSitePlan,
    deleteSitePlan,
    setSitePlanDimension,
    setOpacity,
    setSitePlanScaleLength,
  } = useSitePlan();
  const { toolState, setToolState } = useContext(ToolContext);
  const { removeTransformPathLayers } = useSitePlan();

  const drawScalingLine = () => {
    removeTransformPathLayers();
    setToolState({ ...toolState, activeTool: { ...getAssetStyleInfo("scaling-tool") } });
  };

  const handleScaleLengthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    setSitePlanScaleLength(value);
  };

  const isScalingToolActive = Boolean(
    toolState.activeTool && (toolState.activeTool as { class?: string })?.class === "scaling-tool",
  );

  return (
    <div className="d-flex flex-column">
      {/* File Name Section */}
      <div className="p-3">
        <FormGroup className="mb-0">
          <Label for="fileName">File Name</Label>
          <div>
            <b>{sitePlan?.name}</b>
          </div>
        </FormGroup>
      </div>

      {/* Site Plan Scaling Tool Section */}
      <ListGroupItem className="bg-dark" style={{ textTransform: "uppercase" }}>
        site plan scaling tool
      </ListGroupItem>

      <div className="p-3">
        <div className="mb-3 text-white">
          Input the site plan scale length, click the 'Draw Scaling Line' button, then draw a line
          of the same length on the imported site plan to auto-scale the site plan.
        </div>

        <div className="d-flex align-items-end">
          <div className="flex-fill mr-3">
            <Label for="scaleLength">Scale Length (m)</Label>
            <Input
              type="number"
              step="1"
              min="0"
              id="scaleLength"
              name="scaleLength"
              value={sitePlan?.scaleLength ?? 0}
              onChange={handleScaleLengthChange}
            />
          </div>
          <div className="flex-fill">
            <Button
              color={"primary"}
              active={isScalingToolActive}
              block
              onClick={drawScalingLine}
              style={{ whiteSpace: "nowrap" }}
              disabled={
                !sitePlan?.scaleLength ||
                sitePlan?.scaleLength === 0 ||
                sitePlan?.lockSitePlanPosition
              }
            >
              Draw Scaling Line
            </Button>
          </div>
        </div>
      </div>

      {/* Site Plan Options Section */}
      <ListGroupItem className="bg-dark" style={{ textTransform: "uppercase" }}>
        Site Plan Options
      </ListGroupItem>
      <div className="p-3">
        <FormGroup>
          <Label for="sitePlanOpacity">Opacity</Label>
          <Input
            type="range"
            name="sitePlanOpacity"
            onChange={(e) => setOpacity(parseFloat(e.target.value))}
            min={0.25}
            max={1}
            step={0.05}
            value={sitePlan?.opacity}
          />
        </FormGroup>
        <div className="d-flex mb-3">
          <div className="flex-fill mr-3">
            <FormGroup className="mb-0">
              <Label for="width">Width (m)</Label>
              <Input
                type="number"
                step="1"
                min="1"
                id="width"
                name="width"
                value={sitePlan?.width}
                onChange={(e) => setSitePlanDimension("width", parseFloat(e.target.value))}
              />
            </FormGroup>
          </div>
          <div className="flex-fill">
            <FormGroup className="mb-0">
              <Label for="height">Height (m)</Label>
              <Input
                type="number"
                step="1"
                min="1"
                id="height"
                name="height"
                value={sitePlan?.height}
                onChange={(e) => setSitePlanDimension("height", parseFloat(e.target.value))}
              />
            </FormGroup>
          </div>
        </div>

        {/* Toggle Buttons */}
        <div className="mb-3">
          <Button
            color="dark"
            block
            onClick={() => toggleUniformScaling()}
            className={`mb-2 ${sitePlan?.uniformScaling ? "active" : ""}`}
          >
            <div className="d-flex justify-content-between align-items-center">
              <span>Uniform Scaling</span>
              <i className={`icon-${sitePlan?.uniformScaling ? "check" : "cross"}-solid`}></i>
            </div>
          </Button>
          <Button
            color="dark"
            block
            onClick={() => toggleLockSitePlanPosition()}
            className={sitePlan?.lockSitePlanPosition ? "active" : ""}
          >
            <div className="d-flex justify-content-between align-items-center">
              <span>Lock Site Plan Position</span>
              <i className={`icon-${sitePlan?.lockSitePlanPosition ? "check" : "cross"}-solid`}></i>
            </div>
          </Button>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="p-3">
        <Button color="primary" block className="mb-2" onClick={resetSitePlan}>
          Reset
        </Button>
        <Button id="site-plan-delete" color="danger" block onClick={() => deleteSitePlan()}>
          Delete
        </Button>
      </div>
    </div>
  );
};

export default SitePlanForm;
